import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Upload, Image as ImageIcon, Check } from "lucide-react";

export default function SettingsPage() {
  const { user, tenant, refresh } = useAuth();
  const [logoUrl, setLogoUrl] = useState("");
  const [footerText, setFooterText] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLogoUrl(tenant?.logo_url || "");
    setFooterText(tenant?.footer_text || "");
  }, [tenant]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_000_000) {
      setErr("Please choose an image under 1MB. Tip: a 256×256 PNG or SVG works best.");
      return;
    }
    setErr("");
    const r = new FileReader();
    r.onload = () => setLogoUrl(String(r.result));
    r.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put("/tenant/branding", { logo_url: logoUrl || null, footer_text: footerText || null });
      setSavedAt(new Date());
      if (refresh) refresh();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl fade-in">
      <Card className="p-6 border-slate-100">
        <h2 className="font-extrabold text-[#1F3A6B] text-lg">Workspace</h2>
        <dl className="mt-4 grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-slate-500">Name</dt><dd className="font-semibold text-[#1F3A6B]">{tenant?.name || "—"}</dd>
          <dt className="text-slate-500">Slug</dt><dd className="font-mono text-xs text-slate-700">{tenant?.slug || "—"}</dd>
          <dt className="text-slate-500">Plan</dt><dd className="font-semibold uppercase tracking-wider text-[#1F3A6B]">{tenant?.plan}</dd>
          <dt className="text-slate-500">Industry</dt><dd>{tenant?.industry || "—"}</dd>
          <dt className="text-slate-500">Country</dt><dd>{tenant?.country || "—"}</dd>
        </dl>
      </Card>

      {/* Branding & Logo */}
      <Card className="p-6 border-slate-100" data-testid="settings-branding-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-extrabold text-[#1F3A6B] text-lg">Brand & Logo</h2>
            <p className="text-sm text-slate-600 mt-1">Your logo appears in the header and footer of every published survey. Survey-level overrides are available in the Builder.</p>
          </div>
          {savedAt && <span className="text-xs text-emerald-600 flex items-center gap-1"><Check size={14}/>Saved</span>}
        </div>

        <div className="mt-5 grid sm:grid-cols-[1fr_auto] gap-5 items-start">
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Logo URL or upload</Label>
              <Input
                data-testid="settings-logo-url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://yourcompany.com/logo.png"
                className="mt-1 h-10"
              />
              <div className="mt-2 flex items-center gap-3">
                <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-[#F58220] hover:text-[#1F3A6B] font-semibold">
                  <Upload size={14}/> Upload from device
                  <input data-testid="settings-logo-file" type="file" accept="image/*" className="hidden" onChange={onPickFile}/>
                </label>
                {logoUrl && <button data-testid="settings-logo-clear" onClick={() => setLogoUrl("")} className="text-xs text-rose-600 hover:underline">Remove</button>}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Recommended: a transparent PNG or SVG, max 1MB.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Footer line (optional)</Label>
              <Textarea
                data-testid="settings-footer-text"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                placeholder="© 2026 Acme — Privacy policy at acme.com/privacy. Multiple lines supported."
                rows={3}
                className="mt-1 resize-y min-h-[80px]"
              />
              <p className="mt-1 text-[11px] text-slate-500">Shown in the public survey footer. You can paste multiple lines (legal text, address, etc.).</p>
            </div>
          </div>

          <div className="w-40 sm:w-44">
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Preview</Label>
            <div className="mt-1 h-20 w-full rounded-md border-2 border-dashed border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
              {logoUrl ? (
                <img data-testid="settings-logo-preview" src={logoUrl} alt={tenant?.name || "Logo"} className="max-h-16 max-w-full object-contain"/>
              ) : (
                <ImageIcon size={28} className="text-slate-300"/>
              )}
            </div>
          </div>
        </div>

        {err && <p className="mt-3 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2" data-testid="settings-branding-error">{err}</p>}

        <div className="mt-5 flex justify-end">
          <Button data-testid="settings-branding-save" disabled={busy} onClick={save} className="bg-[#F58220] hover:bg-[#E36F0E] text-white h-10 px-6">
            {busy ? "Saving…" : "Save branding"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-slate-100">
        <h2 className="font-extrabold text-[#1F3A6B] text-lg">Profile</h2>
        <dl className="mt-4 grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-slate-500">Name</dt><dd className="font-semibold text-[#1F3A6B]">{user?.name}</dd>
          <dt className="text-slate-500">Email</dt><dd>{user?.email}</dd>
          <dt className="text-slate-500">Role</dt><dd className="uppercase tracking-wider font-semibold">{user?.role.replace("_"," ")}</dd>
        </dl>
      </Card>

      <Card className="p-6 border-slate-100">
        <h2 className="font-extrabold text-[#1F3A6B] text-lg">Distribution channels</h2>
        <p className="text-sm text-slate-600 mt-1">Configure how surveys reach your respondents.</p>
        <ul className="mt-4 space-y-2 text-sm">
          <li className="flex items-center justify-between border border-slate-100 rounded-md px-4 py-3"><span>Shareable link & QR</span><span className="text-emerald-600 font-bold text-xs">Active</span></li>
          <li className="flex items-center justify-between border border-slate-100 rounded-md px-4 py-3"><span>Email campaigns</span><span className="text-slate-500 font-bold text-xs">Coming soon</span></li>
          <li className="flex items-center justify-between border border-slate-100 rounded-md px-4 py-3"><span>SMS</span><span className="text-slate-500 font-bold text-xs">Coming soon</span></li>
          <li className="flex items-center justify-between border border-slate-100 rounded-md px-4 py-3"><span>WhatsApp</span><span className="text-slate-500 font-bold text-xs">Coming soon</span></li>
        </ul>
      </Card>

      <Card className="p-6 border-slate-100">
        <h2 className="font-extrabold text-[#1F3A6B] text-lg">Billing</h2>
        <p className="text-sm text-slate-600 mt-1">Razorpay-powered subscriptions.</p>
        <p className="mt-3 text-xs text-slate-500">Billing activates when you upgrade. Free plan included by default.</p>
      </Card>

      <ApiKeysCard/>
    </div>
  );
}

/* ---------- API Keys (POS / Billing ingestion) ---------- */
import { Key as KeyIcon, Copy as CopyIcon, Trash2, Plus, ExternalLink, Eye } from "lucide-react";

function ApiKeysCard() {
  const [keys, setKeys] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [defaultSurveyId, setDefaultSurveyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [newKey, setNewKey] = useState(null); // {key, key_prefix, name, id} – shown once
  const [copied, setCopied] = useState("");

  const load = async () => {
    try {
      const [k, s] = await Promise.all([api.get("/tenant/api-keys"), api.get("/surveys")]);
      setKeys(k.data || []);
      setSurveys((s.data || []).filter((x) => x.status === "published"));
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { setErr("Give the key a name (e.g. 'POS – Subway DEL-01')."); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post("/tenant/api-keys", { name: name.trim(), default_survey_id: defaultSurveyId || null });
      setNewKey(r.data);
      setName(""); setDefaultSurveyId("");
      setShowCreate(false);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Apps using it will stop working immediately.")) return;
    try { await api.delete(`/tenant/api-keys/${id}`); load(); } catch (e) { setErr(formatApiError(e)); }
  };

  const copy = (txt, marker) => {
    navigator.clipboard.writeText(txt);
    setCopied(marker);
    setTimeout(() => setCopied(""), 1500);
  };

  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const curlSample = newKey ? `curl -X POST ${base}/api/integrations/transactions \\
  -H "X-API-Key: ${newKey.key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "ref_id": "TXN-2026-998877",
    "mobile_number": "+919876543210",
    "bill_number": "INV-44521",
    "store_code": "DEL-01",
    "bill_date": "2026-06-07",
    "bill_amount": 540.50,
    "bill_type": "Dine-in"
  }'` : "";

  return (
    <Card className="p-6 border-slate-100" data-testid="settings-api-keys-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <KeyIcon size={18} className="text-[#1F3A6B]"/>
            <h2 className="font-extrabold text-[#1F3A6B] text-lg">API Keys (POS / Billing integration)</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1">Push transactions from your POS or billing system. Each ingested transaction returns a personalized survey link you can SMS or WhatsApp to the customer.</p>
        </div>
        <Button data-testid="api-key-new-btn" onClick={() => setShowCreate(true)} className="bg-[#F58220] hover:bg-[#E36F0E] text-white"><Plus size={14} className="mr-1"/>New key</Button>
      </div>

      {err && <p className="mt-3 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">{err}</p>}

      {/* Once-only key reveal */}
      {newKey && (
        <div className="mt-4 border-2 border-emerald-200 bg-emerald-50/60 rounded-lg p-4" data-testid="api-key-revealed">
          <div className="flex items-start gap-2">
            <Eye size={16} className="text-emerald-600 mt-0.5 shrink-0"/>
            <div className="flex-1">
              <p className="text-sm font-bold text-emerald-700">Save this key now — it won't be shown again.</p>
              <div className="mt-2 flex items-center gap-2 bg-white border border-emerald-200 rounded-md px-3 py-2 font-mono text-xs break-all">
                <span className="flex-1 select-all" data-testid="api-key-secret">{newKey.key}</span>
                <button onClick={() => copy(newKey.key, "key")} className="text-emerald-700 hover:bg-emerald-50 rounded px-2 py-1 shrink-0 flex items-center gap-1"><CopyIcon size={12}/>{copied === "key" ? "Copied" : "Copy"}</button>
              </div>
              {curlSample && (
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Test it now (cURL):</p>
                  <div className="relative">
                    <pre className="bg-slate-900 text-emerald-300 text-[11px] rounded-md p-3 overflow-x-auto font-mono leading-relaxed">{curlSample}</pre>
                    <button onClick={() => copy(curlSample, "curl")} className="absolute top-2 right-2 text-slate-300 hover:text-white bg-slate-800 rounded px-2 py-1 text-[11px] flex items-center gap-1"><CopyIcon size={11}/>{copied === "curl" ? "Copied" : "Copy"}</button>
                  </div>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => setNewKey(null)} className="mt-3">Done</Button>
            </div>
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreate && !newKey && (
        <div className="mt-4 border border-slate-200 rounded-lg p-4 bg-slate-50/60">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Name</Label>
              <Input data-testid="api-key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="POS – Subway DEL-01" className="mt-1 h-10"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Default survey (optional)</Label>
              <select data-testid="api-key-survey" value={defaultSurveyId} onChange={(e) => setDefaultSurveyId(e.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="">— No default (must pass survey_id per request) —</option>
                {surveys.map((s) => <option key={s.id} value={s.id}>{(s.title || "").replace(/<[^>]+>/g, "").slice(0, 60)}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button data-testid="api-key-create-submit" onClick={create} disabled={busy} className="bg-[#F58220] hover:bg-[#E36F0E] text-white">{busy ? "Creating…" : "Create key"}</Button>
            <Button variant="ghost" onClick={() => { setShowCreate(false); setErr(""); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Keys list */}
      <div className="mt-4 space-y-2" data-testid="api-keys-list">
        {keys.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-md p-6 text-center text-sm text-slate-500">No API keys yet. Click <span className="font-semibold">New key</span> to create one.</div>
        ) : (
          keys.map((k) => {
            const survey = surveys.find((s) => s.id === k.default_survey_id);
            const revoked = !!k.revoked_at;
            return (
              <div key={k.id} data-testid={`api-key-row-${k.id}`} className={`border rounded-md p-3 flex items-center gap-3 ${revoked ? "border-slate-100 bg-slate-50/50 opacity-60" : "border-slate-200 bg-white"}`}>
                <KeyIcon size={16} className={revoked ? "text-slate-400" : "text-[#F58220]"}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#1F3A6B] truncate">{k.name}</p>
                    {revoked && <span className="text-[10px] font-bold uppercase text-rose-600 bg-rose-50 border border-rose-100 px-2 py-0.5 rounded-full">Revoked</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
                    <span className="font-mono">{k.key_prefix}<span className="text-slate-400">••••••••••••</span></span>
                    {survey && <span>→ {(survey.title || "").replace(/<[^>]+>/g, "").slice(0, 40)}</span>}
                    {k.last_used_at && <span>· last used {new Date(k.last_used_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                {!revoked && (
                  <Button data-testid={`api-key-revoke-${k.id}`} size="icon" variant="ghost" className="h-8 w-8 text-rose-600 hover:bg-rose-50" onClick={() => revoke(k.id)} title="Revoke key">
                    <Trash2 size={14}/>
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>

      <details className="mt-4 group" data-testid="api-docs">
        <summary className="cursor-pointer text-sm font-semibold text-[#1F3A6B] flex items-center gap-1 hover:underline">
          <ExternalLink size={14}/> API documentation
        </summary>
        <div className="mt-3 space-y-3 text-sm text-slate-700">
          <p><span className="font-semibold">Endpoint:</span> <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono text-xs">POST {base}/api/integrations/transactions</code></p>
          <p><span className="font-semibold">Auth:</span> Send your API key as <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono text-xs">X-API-Key: nps_...</code></p>
          <div>
            <p className="font-semibold mb-1">Request body:</p>
            <pre className="bg-slate-900 text-white/70 text-[11px] rounded-md p-3 overflow-x-auto font-mono">{`{
  "ref_id":         "TXN-2026-998877",      // your unique transaction id
  "mobile_number":  "+919876543210",
  "bill_number":    "INV-44521",
  "store_code":     "DEL-01",
  "bill_date":      "2026-06-07",
  "bill_amount":    540.50,
  "bill_type":      "Dine-in",
  "survey_id":      null                     // optional override; otherwise uses API key default
}`}</pre>
          </div>
          <div>
            <p className="font-semibold mb-1">Response:</p>
            <pre className="bg-slate-900 text-emerald-300 text-[11px] rounded-md p-3 overflow-x-auto font-mono">{`{
  "ok": true,
  "transaction_id": "tx_abc123",
  "ref_id": "TXN-2026-998877",
  "survey_url": "https://npscore.ai/r/<slug>?ref=TXN-2026-998877&store=DEL-01&bill=INV-44521&bill_type=Dine-in",
  "received_at": "2026-06-26T09:55:53Z"
}`}</pre>
          </div>
          <p className="text-xs text-slate-500">SMS/WhatsApp the returned <code>survey_url</code> to your customer. When they respond, the transaction is auto-linked and the NPS score appears in <span className="font-semibold">Transactions</span>.</p>
        </div>
      </details>
    </Card>
  );
}

